interface NamedThing { name?: string | null }

/**
 * How a ticket's seat category and price band read together.
 *
 * The tier is the seat category ("Standard"); the band is what the customer
 * actually chose and paid for ("Adult", "Child"). Showing the tier alone left
 * a basket of four tickets all reading "Standard" with two different prices,
 * and nothing to say which was which — the customer had to work it out from
 * the money.
 *
 * Events created before price bands existed have no band, and their tier IS
 * the price. Those fall back to the tier name alone rather than repeating it.
 */
export const tierAndBand = (
  tier?: NamedThing | null,
  band?: NamedThing | null,
): string => {
  const tierName = tier?.name?.trim();
  const bandName = band?.name?.trim();

  if (!tierName) return bandName || '';
  if (!bandName || bandName === tierName) return tierName;
  return `${tierName} · ${bandName}`;
};

/** The full line for a ticket, e.g. "Ticket: Spring Gala (Standard · Adult)". */
export const eventTicketLabel = (
  eventName?: string | null,
  tier?: NamedThing | null,
  band?: NamedThing | null,
): string => {
  const detail = tierAndBand(tier, band);
  return detail ? `Ticket: ${eventName} (${detail})` : `Ticket: ${eventName}`;
};

interface EventAddress {
  name?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  postCode?: string | null;
}

/**
 * An event's venue on one line.
 *
 * Returns an empty string when the event has no address, which is the case
 * that mattered: the address column object always exists with null fields, so
 * a truthiness check on it passed and rendered a map pin with nothing beside
 * it. Callers should test the returned string, not the address object.
 */
export const formatEventAddress = (address?: EventAddress | null): string => {
  if (!address) return '';
  return [address.name, address.line1, address.city, address.postCode]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
};

export default eventTicketLabel;
